/**
 * useTaskMutationsGraphQL Hooks
 *
 * Apollo Client hooks for task-related mutations.
 * Feature: 648-task-triggers (User Story 2)
 */

import { useMutation, gql } from '@apollo/client';
import { useCallback } from 'react';
import logger from '@/utils/logger';
import { invalidateForMutation } from '../cache-utils';
import { useApolloClientSafe } from '../cache-utils';

/**
 * GraphQL Mutations
 */

/**
 * Create a task mutation with duplicate detection
 * Returns { task, created } where created is false if returning existing task
 */
const CREATE_TASK_MUTATION = gql`
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input) {
      task
      created
    }
  }
`;

/**
 * Input type for createTask mutation
 */
export interface CreateTaskInput {
  actionType: string;
  targetType: string;
  targetId: string;
  targetContext?: Record<string, unknown>;
  description?: string;
  progressRequired?: number;
}

/**
 * Task object from GraphQL response
 */
interface TaskResult {
  id: string;
  globalId: string;
  actionType: string;
  status: string;
  targetType: string;
  targetId: string;
  targetUrl?: string;
  targetContext?: Record<string, unknown>;
  description?: string;
  progressCurrent: number;
  progressRequired?: number;
  sourceType: string;
  sourceId?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  hasProgress: boolean;
  progressPercent?: number;
}

/**
 * Response type for createTask mutation
 */
interface CreateTaskResult {
  createTask: {
    task: TaskResult;
    created: boolean;
  };
}

/**
 * Return type for useCreateTaskGraphQL hook
 */
interface CreateTaskHookResult {
  createTask: (input: CreateTaskInput) => Promise<{
    task: TaskResult;
    created: boolean;
  }>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook to create a task with duplicate detection
 *
 * If a pending task already exists for the same user, actionType, targetType, and targetId,
 * returns the existing task with created: false instead of creating a duplicate.
 *
 * @returns Object with createTask function, loading state, and error
 *
 * @example
 * ```tsx
 * const { createTask, loading } = useCreateTaskGraphQL();
 *
 * const handleClick = async () => {
 *   const { task, created } = await createTask({
 *     actionType: 'COMMENT_ON_PHOTO',
 *     targetType: 'photo',
 *     targetId: photoGlobalId,
 *   });
 *
 *   if (created) {
 *     toast.success('Task created');
 *   } else {
 *     toast.info('Task already pending');
 *   }
 * };
 * ```
 */
export function useCreateTaskGraphQL(): CreateTaskHookResult {
  const apolloClient = useApolloClientSafe();

  const [mutate, { loading, error }] = useMutation<CreateTaskResult>(CREATE_TASK_MUTATION);

  const createTask = useCallback(
    async (input: CreateTaskInput) => {
      logger.info('Tasks', 'Creating task', {
        actionType: input.actionType,
        targetType: input.targetType,
        targetId: input.targetId,
      });

      const result = await mutate({
        variables: { input },
      });

      if (!result.data) {
        throw new Error('No data returned from createTask mutation');
      }

      const { task, created } = result.data.createTask;

      logger.info('Tasks', created ? 'Task created' : 'Returning existing task', {
        taskId: task.id,
        created,
      });

      // Invalidate task-related queries
      if (apolloClient) {
        await invalidateForMutation(apolloClient, 'createTask');
      }

      return { task, created };
    },
    [mutate, apolloClient]
  );

  return {
    createTask,
    loading,
    error: error as Error | undefined,
  };
}
